import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  Select,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function AddRawMaterial() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  // Get tab from URL params
  const searchParams = new URLSearchParams(window.location.search);
  const tabFromUrl = searchParams.get('tab') || '2';

  const [formData, setFormData] = useState({
    materialName: "",
    description: "",
    unit: "units",
  });

  const [loading, setLoading] = useState(false);

  const unitOptions = ["units", "meters", "kg", "liters", "pieces", "yards", "rolls"];

  useEffect(() => {
    if (isEdit) {
      fetchMaterialDetails();
    }
  }, [id]);

  const fetchMaterialDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/inventory/raw-materials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const material = data.material;
        
        setFormData({
          materialName: material.MaterialName,
          description: material.Description || "",
          unit: material.Unit || "units",
        });
      }
    } catch (error) {
      console.error("Error fetching material details:", error);
      toast.error("Failed to load material details");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.materialName.trim()) {
      toast.error("Please enter material name");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const endpoint = isEdit
        ? `${API_BASE_URL}/api/inventory/raw-materials/${id}`
        : `${API_BASE_URL}/api/inventory/raw-materials`;

      const response = await fetch(endpoint, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(`Raw material ${isEdit ? "updated" : "created"} successfully`);
        navigate(`/inventory?tab=${tabFromUrl}`);
      } else {
        const data = await response.json();
        toast.error(data.message || "Failed to save raw material");
      }
    } catch (error) {
      console.error("Error saving raw material:", error);
      toast.error("Failed to save raw material");
    } finally {
      setLoading(false);
    }
  };

  // Dark Theme Styles
  const darkTextField = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#3a3a3a",
      borderRadius: 1,
      "& fieldset": {
        borderColor: "rgba(196, 165, 116, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(196, 165, 116, 0.4)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#C4A574",
      },
    },
    "& .MuiOutlinedInput-input": {
      color: "#fff",
      fontSize: { xs: "0.9rem", sm: "1rem" },
      "&::placeholder": {
        color: "rgba(196, 165, 116, 0.5)",
        opacity: 1,
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "#C4A574",
    },
  };

  const darkSelect = {
    backgroundColor: "#3a3a3a",
    color: "#fff",
    fontSize: { xs: "0.9rem", sm: "1rem" },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(196, 165, 116, 0.2)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(196, 165, 116, 0.4)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#C4A574",
    },
    "& .MuiSvgIcon-root": {
      color: "#C4A574",
    },
  };

  return (
    <AppLayout>
      <Box
        sx={{
          minHeight: "calc(100vh - 80px)",
          p: { xs: 1.5, sm: 2.5, md: 4 },
          background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
          maxWidth: { xs: "100%", sm: 700, md: 800 },
          mx: "auto"
        }}
      >
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/inventory?tab=${tabFromUrl}`)}
          sx={{
            color: "#C4A574",
            mb: 3,
            fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.95rem" },
            textTransform: 'none',
            "&:hover": { bgcolor: "rgba(196, 165, 116, 0.1)" },
          }}
        >
          Back to Inventory
        </Button>

        {/* Title */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            color: "#C4A574",
            textAlign: "center",
            mb: 4,
            textDecoration: "underline",
            textUnderlineOffset: 8,
            textDecorationThickness: 2,
            fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
          }}
        >
          {isEdit ? "EDIT RAW MATERIAL" : "ADD NEW RAW MATERIAL"}
        </Typography>

        <form onSubmit={handleSubmit}>
          {/* MATERIAL INFORMATION */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: 4,
              backgroundColor: "#2a2a2a",
              border: "1px solid rgba(196, 165, 116, 0.2)",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: "#C4A574",
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                letterSpacing: 0.5,
              }}
            >
              Material Information
            </Typography>

            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              {/* Material Name Field */}
              <Grid item xs={12}>
                <Typography
                  sx={{
                    color: "#C4A574",
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  }}
                >
                  Material Name *
                </Typography>
                <TextField
                  placeholder="Enter material name"
                  value={formData.materialName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      materialName: e.target.value,
                    })
                  }
                  fullWidth
                  required
                  disabled={loading}
                  sx={darkTextField}
                />
              </Grid>

              {/* Description Field */}
              <Grid item xs={12}>
                <Typography
                  sx={{
                    color: "#C4A574",
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  }}
                >
                  Description
                </Typography>
                <TextField
                  placeholder="Enter material description (optional)"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  fullWidth
                  multiline
                  rows={4}
                  disabled={loading}
                  sx={darkTextField}
                />
              </Grid>

              {/* Unit Field */}
              <Grid item xs={12} md={6}>
                <Typography
                  sx={{
                    color: "#C4A574",
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  }}
                >
                  Unit of Measurement *
                </Typography>
                <FormControl fullWidth required disabled={loading}>
                  <Select
                    value={formData.unit}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        unit: e.target.value,
                      })
                    }
                    sx={darkSelect}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: "#2a2a2a",
                          border: "1px solid rgba(196, 165, 116, 0.2)",
                          "& .MuiMenuItem-root": {
                            color: "#fff",
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            "&:hover": { bgcolor: "#333" },
                            "&.Mui-selected": { bgcolor: "#C4A574", color: "#000" },
                          },
                        },
                      },
                    }}
                  >
                    {unitOptions.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit.charAt(0).toUpperCase() + unit.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#999",
                    mt: 1,
                    display: "block",
                    fontSize: { xs: "0.75rem", sm: "0.8rem" },
                  }}
                >
                  Select the unit in which this material will be measured
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* INFO BOX */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mb: 4,
              backgroundColor: "rgba(196, 165, 116, 0.08)",
              border: "1px solid rgba(196, 165, 116, 0.2)",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#C4A574",
                fontWeight: 600,
                mb: 1,
                fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.95rem" },
              }}
            >
              💡 Tip
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#999",
                fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.9rem" },
                lineHeight: 1.6,
              }}
            >
              Raw materials are used in customized products. Make sure to
              select the correct unit of measurement as it will be used
              throughout the system for tracking and inventory management.
            </Typography>
          </Paper>

          {/* ACTION BUTTONS */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2 },
              justifyContent: "center",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(`/inventory?tab=${tabFromUrl}`)}
              size="large"
              disabled={loading}
              sx={{
                px: { xs: 2, md: 4 },
                py: { xs: 1.2, md: 1.5 },
                borderColor: "#C4A574",
                color: "#C4A574",
                fontWeight: 700,
                fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
                textTransform: 'none',
                "&:hover": {
                  backgroundColor: "rgba(196, 165, 116, 0.1)",
                  borderColor: "#C4A574",
                },
                "&:disabled": {
                  borderColor: "rgba(196, 165, 116, 0.3)",
                  color: "rgba(196, 165, 116, 0.3)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={loading}
              sx={{
                px: { xs: 2, md: 4 },
                py: { xs: 1.2, md: 1.5 },
                bgcolor: "#C4A574",
                color: "#000",
                fontWeight: 700,
                fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
                textTransform: 'none',
                "&:hover": { bgcolor: "#B39565" },
                "&:disabled": {
                  bgcolor: "rgba(196, 165, 116, 0.5)",
                },
              }}
            >
              {loading
                ? "Processing..."
                : isEdit
                ? "Update Material"
                : "Create Material"}
            </Button>
          </Box>
        </form>

        {/* Decorative Fade */}
        <Box
          sx={{
            mt: 6,
            height: 40,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.3))",
            borderRadius: 2,
          }}
        />
      </Box>
    </AppLayout>
  );
}
